import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';

const RestaurantMenuPage = ({ isLoggedIn }) => {
  const { restaurantId } = useParams();
  const navigate = useNavigate();

  const [restaurant, setRestaurant] = useState(null);
  const [menuItems, setMenuItems] = useState([]);
  const [categories, setCategories] = useState([]);
  const [activeCategory, setActiveCategory] = useState('all');
  const [loading, setLoading] = useState(true);
  const [cart, setCart] = useState([]);
  const [showCart, setShowCart] = useState(false);
  const [customizing, setCustomizing] = useState(null);

  useEffect(() => {
    const fetchMenuData = async () => {
      try {
        const response = await fetch(`https://food.mohamadmansour.me/api/restaurant/${restaurantId}/menu`);
        const data = await response.json();

        const restaurantInfo = {
          id: restaurantId,
          name: "Restaurant Name", // Optional: add this from backend if needed
          banner: "/assets/images/default-banner.jpg",
          logo: "/assets/images/default-logo.png",
          description: "Restaurant description here...",
          rating: 4.7,
          reviews: 230,
          deliveryTime: "30-45 min",
          deliveryFee: 2.99,
          minOrder: 10.0
        };

        setRestaurant(restaurantInfo);
        setMenuItems(data.products);
        setCategories(data.categories);
      } catch (err) {
        console.error("Failed to load restaurant menu", err);
      } finally {
        setLoading(false);
      }
    };

    fetchMenuData();
  }, [restaurantId]);
  useEffect(() => {
    const storedCart = localStorage.getItem('cart');
    if (storedCart) {
      try {
        const parsedCart = JSON.parse(storedCart);
        setCart(parsedCart.products ?? parsedCart); // Support both formats
      } catch (err) {
        console.error('Error parsing cart from localStorage:', err);
      }
    }
  }, []);

  const saveCart = (updatedCart) => {
    console.log(updatedCart);
    setCart(updatedCart);

    const cartData = {
      restaurant_id: restaurantId,
      products: updatedCart
    };

    localStorage.setItem('cart', JSON.stringify(cartData));
  };



  const handleAddToCart = (item) => {
    if (!isLoggedIn) {
      // Redirect to login page if not logged in
      alert('Please log in to add items to your cart');
      navigate('/login');
      return;
    }

    if (item.customizable) {
      setCustomizing({
        ...item,
        image: item.image,
        selectedOptions: item.options.map(option => ({
          name: option.name,
          selected: option.defaultValue || (option.multiple ? [] : '')
        }))
      });
    } else {
      // Add directly to cart if not customizable
      saveCart([...cart, {
        id: item.id,
        name: item.name,
        price: item.price,
        quantity: 1,
        options: [],
        image: item.image
      }]);

      // Show cart after adding item
      setShowCart(true);
    }
  };

  const handleAddCustomizedItem = () => {
    if (!customizing) return;

    if (typeof customizing.price !== 'number') {
      alert('Error: Base price is missing. Please try again later.');
      return;
    }

    let totalPrice = customizing.price;
    const selectedOptions = [];

    customizing.selectedOptions.forEach((selected, index) => {
      const option = customizing.options[index];

      if (option.multiple && Array.isArray(selected.selected)) {
        selected.selected.forEach(choiceName => {
          const choice = option.choices.find(c => c.name === choiceName);
          if (choice) {
            totalPrice += choice.price;
            selectedOptions.push({
              name: option.name,
              choice: choice.name,
              price: choice.price
            });
          }
        });
      } else {
        const choice = option.choices.find(c => c.name === selected.selected);
        if (choice) {
          totalPrice += choice.price;
          selectedOptions.push({
            name: option.name,
            choice: choice.name,
            price: choice.price
          });
        }
      }
    });

    const newItem = {
      product_id: customizing.id,
      name: customizing.name,
      basePrice: customizing.price,
      finalPrice: totalPrice,
      quantity: 1,
      options: selectedOptions,
      image: customizing.image
    };

    // ✅ Check if an identical item already exists in the cart
    const existingIndex = cart.findIndex(item =>
      item.product_id === newItem.product_id &&
      JSON.stringify(item.options) === JSON.stringify(newItem.options)
    );

    let updatedCart;
    if (existingIndex !== -1) {
      // Increment quantity if same product and same customization
      updatedCart = [...cart];
      updatedCart[existingIndex].quantity += 1;
    } else {
      // Otherwise, add new item
      updatedCart = [...cart, newItem];
    }

    // Save and update UI
    saveCart(updatedCart);
    setCustomizing(null);
    setShowCart(true);
  };




  const handleCustomizationChange = (optionIndex, value) => {
    if (!customizing) return;

    const updatedOptions = [...customizing.selectedOptions];
    const option = customizing.options[optionIndex];

    if (option.multiple) {
      // Handle multiple selections (checkboxes)
      const currentSelections = Array.isArray(updatedOptions[optionIndex].selected)
        ? [...updatedOptions[optionIndex].selected]
        : [];

      if (currentSelections.includes(value)) {
        // Remove if already selected
        updatedOptions[optionIndex].selected = currentSelections.filter(item => item !== value);
      } else {
        // Add if not already selected
        updatedOptions[optionIndex].selected = [...currentSelections, value];
      }
    } else {
      // Handle single selection (radio/select)
      updatedOptions[optionIndex].selected = value;
    }

    setCustomizing({
      ...customizing,
      selectedOptions: updatedOptions
    });
  };

  const handleRemoveItem = (index) => {
    const newCart = [...cart];
    newCart.splice(index, 1);
    saveCart(newCart);
  };

  const handleUpdateQuantity = (index, newQuantity) => {
    if (newQuantity < 1) return;

    const newCart = [...cart];
    newCart[index].quantity = newQuantity;
    saveCart(newCart);
  };

  const handleCheckout = () => {
    if (cart.length === 0) {
      alert('Your cart is empty');
      return;
    }

    // Navigate to checkout page
    navigate('/checkout');
  };

  const calculateCartTotal = () => {
    return cart.reduce((total, item) => total + (item.price * item.quantity), 0);
  };

  const filterMenuItems = () => {
    if (activeCategory === 'all') {
      return menuItems;
    }
    return menuItems.filter(item => item.category === activeCategory);
  };

  if (loading) {
    return (
      <div className="restaurant-menu-page">
        <div className="loading-container">
          <div className="loading-spinner"></div>
          <p>Loading restaurant menu...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="restaurant-menu-page">
      {/* Restaurant Banner */}
      <div className="restaurant-banner">
        <div className="banner-overlay"></div>
        <div className="restaurant-info">
          <div className="restaurant-logo">
            {/* In a real app, this would be the actual logo */}
            <i className="fas fa-pizza-slice"></i>
          </div>
          <div className="restaurant-details">
            <h1>{restaurant.name}</h1>
            <p className="restaurant-description">{restaurant.description}</p>
            <div className="restaurant-meta">
              <span className="meta-item">
                <i className="fas fa-star"></i> {restaurant.rating} ({restaurant.reviews} reviews)
              </span>
              <span className="meta-item">
                <i className="fas fa-clock"></i> {restaurant.deliveryTime}
              </span>
              <span className="meta-item">
                <i className="fas fa-truck"></i> ${restaurant.deliveryFee.toFixed(2)} delivery
              </span>
              <span className="meta-item">
                <i className="fas fa-dollar-sign"></i> ${restaurant.minOrder.toFixed(2)} min. order
              </span>
            </div>
          </div>
        </div>
      </div>

      {/* Menu Categories */}
      <div className="menu-categories">
        {categories.map(category => (
          <button
            key={category}
            className={`category-btn ${activeCategory === category ? 'active' : ''}`}
            onClick={() => setActiveCategory(category)}
          >
            {category.charAt(0).toUpperCase() + category.slice(1)}
          </button>
        ))}
      </div>

      {/* Menu Items */}
      <div className="menu-items-container">
        {filterMenuItems().map(item => (
          <div key={item.id} className="menu-item-card">
            <div className="item-image">
              {/* Placeholder for food image */}
              <div className="placeholder-image">
                <i className={`fas fa-${item.category === 'pizzas' ? 'pizza-slice' :
                  item.category === 'beverages' ? 'glass' :
                    item.category === 'desserts' ? 'birthday-cake' :
                      item.category === 'sides' ? 'bread-slice' :
                        item.category === 'salads' ? 'leaf' : 'utensils'}`}></i>
              </div>
            </div>
            <div className="item-details">
              <h3 className="item-name">{item.name}</h3>
              <p className="item-description">{item.description}</p>
              <div className="item-price">${(item.price ?? 0).toFixed(2)}</div>
              <button
                className="btn btn-add-to-cart"
                onClick={() => handleAddToCart(item)}
              >
                Add to Cart
              </button>
            </div>
          </div>
        ))}
      </div>

      {/* Floating Cart Icon */}
      <div className="cart-icon" onClick={() => setShowCart(!showCart)}>
        <i className="fas fa-shopping-cart"></i>
        {cart.length > 0 && (
          <span className="cart-badge">
            {cart.reduce((sum, item) => sum + (item.quantity ?? 1), 0)}
          </span>
        )}
      </div>



      {/* Cart Sidebar */}
      {showCart && (
        <div className="cart-sidebar">
          <div className="cart-header">
            <h2>Your Cart</h2>
            <button className="btn-close" onClick={() => setShowCart(false)}>
              <i className="fas fa-times"></i>
            </button>
          </div>

          {cart.length === 0 ? (
            <div className="empty-cart">
              <i className="fas fa-shopping-cart"></i>
              <p>Your cart is empty</p>
              <button
                className="btn btn-primary"
                onClick={() => setShowCart(false)}
              >
                Start Ordering
              </button>
            </div>
          ) : (
            <>
              <div className="cart-items">
                {cart.map((item, index) => (
                  <div key={index} className="cart-item">
                    <div className="item-info">
                      <h4>{item.name}</h4>
                      {item.options.length > 0 && (
                        <div className="item-options">
                          {item.options.map((opt, i) => (
                            <span key={i} className="option-tag">
                              {opt.option}: {opt.choice}
                              {opt.price !== 0 && (
                                <span className="option-price">
                                  ({opt.price > 0 ? `+${(opt.price ?? 0).toFixed(2)}` : `${(opt.price ?? 0).toFixed(2)}`})
                                </span>
                              )}
                            </span>
                          ))}
                        </div>
                      )}

                      <div className="item-price">${(item.price ?? 0).toFixed(2)}</div>
                    </div>
                    <div className="item-actions">
                      <div className="quantity-control">
                        <button
                          className="btn-quantity"
                          onClick={() => handleUpdateQuantity(index, item.quantity - 1)}
                        >
                          -
                        </button>
                        <span className="quantity">{item.quantity}</span>
                        <button
                          className="btn-quantity"
                          onClick={() => handleUpdateQuantity(index, item.quantity + 1)}
                        >
                          +
                        </button>
                      </div>
                      <button
                        className="btn-remove"
                        onClick={() => handleRemoveItem(index)}
                      >
                        <i className="fas fa-trash"></i>
                      </button>
                    </div>
                  </div>
                ))}
              </div>

              <div className="cart-footer">
                <div className="cart-total">
                  <span>Total:</span>
                  <span>${calculateCartTotal().toFixed(2)}</span>
                </div>
                <button
                  className="btn btn-primary btn-checkout"
                  onClick={handleCheckout}
                >
                  Proceed to Checkout
                </button>
              </div>
            </>
          )}
        </div>
      )}

      {/* Customization Modal */}
      {customizing && (
        <div className="customization-modal">
          <div className="modal-content">
            <div className="modal-header">
              <h2>Customize Your {customizing.name}</h2>
              <button
                className="btn-close"
                onClick={() => setCustomizing(null)}
              >
                <i className="fas fa-times"></i>
              </button>
            </div>
            {customizing.image && (
              <div className="modal-image-preview">
                <img src={customizing.image} alt={customizing.name} style={{ width: '50%', borderRadius: '12px', marginBottom: '1rem' }} />
              </div>
            )}
            <div className="modal-body">
              {customizing.options.map((option, optionIndex) => (
                <div key={optionIndex} className="customization-option">
                  <h3>{option.name} {option.required && <span className="required">*</span>}</h3>
                  <div className="option-choices">
                    {option.multiple ? (
                      // Checkboxes for multiple selection
                      option.choices.map((choice, choiceIndex) => {
                        const isSelected = Array.isArray(customizing.selectedOptions[optionIndex].selected) &&
                          customizing.selectedOptions[optionIndex].selected.includes(choice.name);

                        return (
                          <label key={choiceIndex} className="checkbox-label">
                            <input
                              type="checkbox"
                              checked={isSelected}
                              onChange={() => handleCustomizationChange(optionIndex, choice.name)}
                            />
                            <span className="choice-name">{choice.name}</span>
                            {choice.price > 0 && (
                              <span className="choice-price">+${(choice.price ?? 0).toFixed(2)}</span>
                            )}
                            {choice.price < 0 && (
                              <span className="choice-price">${(choice.price ?? 0).toFixed(2)}</span>
                            )}
                          </label>
                        );
                      })
                    ) : (
                      // Radio buttons for single selection
                      option.choices.map((choice, choiceIndex) => (
                        <label key={choiceIndex} className="radio-label">
                          <input
                            type="radio"
                            name={`option-${optionIndex}`}
                            checked={customizing.selectedOptions[optionIndex].selected === choice.name}
                            onChange={() => handleCustomizationChange(optionIndex, choice.name)}
                          />
                          <span className="choice-name">{choice.name}</span>
                          {choice.price > 0 && (
                            <span className="choice-price">+${(choice.price ?? 0).toFixed(2)}</span>
                          )}
                          {choice.price < 0 && (
                            <span className="choice-price">${(choice.price ?? 0).toFixed(2)}</span>
                          )}
                        </label>
                      ))
                    )}
                  </div>
                </div>
              ))}
            </div>

            <div className="modal-footer">
              <button
                className="btn btn-outline"
                onClick={() => setCustomizing(null)}
              >
                Cancel
              </button>
              <button
                className="btn btn-primary"
                onClick={handleAddCustomizedItem}
              >
                Add to Cart
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default RestaurantMenuPage;